import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, List, ChevronLeft, ChevronRight, Sparkles, Clock } from 'lucide-react';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
const BIRTHDAY_CALENDAR_ID = process.env.REACT_APP_BIRTHDAY_CALENDAR_ID || '';

// Indian Holidays Fallback
const MOCK_INDIAN_HOLIDAYS = [
  { id: 'h1', summary: 'Republic Day', start: { date: '2026-01-26' }, description: 'National Holiday' },
  { id: 'h2', summary: 'Maha Shivratri', start: { date: '2026-02-15' }, description: 'Gazetted Holiday' },
  { id: 'h3', summary: 'Holi', start: { date: '2026-03-04' }, description: 'Festival of Colors' },
  { id: 'h4', summary: 'Good Friday', start: { date: '2026-04-03' }, description: 'Christian Holiday' },
  { id: 'h5', summary: 'Eid ul-Fitr', start: { date: '2026-04-20' }, description: 'Islamic Festival' },
  { id: 'h6', summary: 'Independence Day', start: { date: '2026-08-15' }, description: 'National Independence Day celebration' },
  { id: 'h7', summary: 'Gandhi Jayanti', start: { date: '2026-10-02' }, description: 'Mahatma Gandhi Birthday' },
  { id: 'h8', summary: 'Dussehra', start: { date: '2026-10-20' }, description: 'Vijayadashami festival' },
  { id: 'h9', summary: 'Diwali / Deepavali', start: { date: '2026-11-08' }, description: 'Festival of Lights' },
  { id: 'h10', summary: 'Christmas Day', start: { date: '2026-12-25' }, description: 'Christian Festival' }
];

// Birthdays Fallback
const MOCK_BIRTHDAYS = [
  { id: 'b1', summary: 'Arnav Sharma Birthday', start: { date: '2026-01-14' }, description: 'Class III Student Birthday' },
  { id: 'b2', summary: 'Principal Madam Birthday', start: { date: '2026-03-22' }, description: 'Celebrating our respected Principal' },
  { id: 'b3', summary: 'Vamshee Krishna Birthday', start: { date: '2026-07-25' }, description: 'Primary Section Coordinator' },
  { id: 'b4', summary: 'Sneha Reddy Birthday', start: { date: '2026-10-12' }, description: 'Preschool Teacher Birthday' }
];

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarEvents();
  }, []);

  const fetchCalendarEvents = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const timeMin = new Date(now.getFullYear(), 0, 1).toISOString();
      const timeMax = new Date(now.getFullYear(), 11, 31, 23, 59, 59).toISOString();

      let loadedEvents = [];

      // ===============================
      // Indian Holidays
      // ===============================
      if (GOOGLE_API_KEY) {
        try {
          const holidaysRes = await axios.get(
            `https://www.googleapis.com/calendar/v3/calendars/en.indian%23holiday%40group.v.calendar.google.com/events`,
            {
              params: {
                key: GOOGLE_API_KEY,
                singleEvents: true,
                orderBy: 'startTime',
                timeMin,
                timeMax,
                maxResults: 250,
              },
            }
          );
          const holidays = holidaysRes.data.items.map((item) => ({
            ...item,
            isHoliday: true,
            isBirthday: false,
          }));
          loadedEvents.push(...holidays);
        } catch (err) {
          console.warn('Failed to load Google Indian Holidays API, using mocks.', err);
          loadedEvents.push(...MOCK_INDIAN_HOLIDAYS.map(h => ({ ...h, isHoliday: true, isBirthday: false })));
        }
      } else {
        loadedEvents.push(...MOCK_INDIAN_HOLIDAYS.map(h => ({ ...h, isHoliday: true, isBirthday: false })));
      }

      // ===============================
      // Birthdays
      // ===============================
      if (GOOGLE_API_KEY && BIRTHDAY_CALENDAR_ID) {
        try {
          const birthdayRes = await axios.get(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(BIRTHDAY_CALENDAR_ID)}/events`,
            {
              params: {
                key: GOOGLE_API_KEY,
                singleEvents: true,
                orderBy: 'startTime',
                timeMin,
                timeMax,
                maxResults: 500,
              },
            }
          );
          const birthdays = birthdayRes.data.items.map((item) => ({
            ...item,
            isHoliday: false,
            isBirthday: true,
          }));
          loadedEvents.push(...birthdays);
        } catch (err) {
          console.error("Failed to load birthday calendar:", err.response?.data || err);
          loadedEvents.push(...MOCK_BIRTHDAYS.map(b => ({ ...b, isHoliday: false, isBirthday: true })));
        }
      } else {
        loadedEvents.push(...MOCK_BIRTHDAYS.map(b => ({ ...b, isHoliday: false, isBirthday: true })));
      }

      // ===============================
      // Sort by date
      // ===============================
      loadedEvents.sort(
        (a, b) =>
          new Date(a.start.date || a.start.dateTime) -
          new Date(b.start.date || b.start.dateTime)
      );

      setEvents(loadedEvents);
    } catch (err) {
      console.error('Failed to load calendar data:', err.response?.data || err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDayEvents(null);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDayEvents(null);
  };

  // Process Calendar Grid Days
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayIndex = getFirstDayOfMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();

  const calendarDays = [];
  // Empty slots for previous month padding
  for (let i = 0; i < firstDayIndex; i++) {
    calendarDays.push(null);
  }
  // Days of current month
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push(d);
  }

  // Get events on a specific day
  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => {
      const eventStart = event.start.date || event.start.dateTime;
      return eventStart && eventStart.startsWith(dateStr);
    });
  };

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-contact)', paddingTop: '120px', paddingBottom: '80px', fontFamily: 'Quicksand' }}>

      {/* Floating background details */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>
      <div className="page-container-wide" style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: '40px' }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary-dark)', textTransform: 'uppercase', letterSpacing: '1px' }}>School Agenda</span>
          <h1 style={{ fontSize: '2.5rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Events & Holidays Calendar</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: '1.05rem' }}>Keep track of school activities, scheduled test dates, and national holidays.</p>
        </motion.div>

        {/* Control Panel Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px', backgroundColor: '#ffffff', padding: '16px 24px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
          {/* Month Navigator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={prevMonth} style={navBtnStyle}><ChevronLeft size={20} /></button>
            <h2 style={{ margin: 0, fontSize: '1.35rem', width: '160px', textAlign: 'center', color: 'var(--text-primary)', fontWeight: 800, fontFamily: 'Poppins' }}>
              {monthName} {year}
            </h2>
            <button onClick={nextMonth} style={navBtnStyle}><ChevronRight size={20} /></button>
          </div>

          {/* Grid/List View Toggler */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setViewMode('grid')} style={viewMode === 'grid' ? activeToggleBtnStyle : toggleBtnStyle}>
              <CalendarIcon size={16} /> Grid View
            </button>
            <button onClick={() => setViewMode('list')} style={viewMode === 'list' ? activeToggleBtnStyle : toggleBtnStyle}>
              <List size={16} /> List View
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            Loading Event Feed...
          </div>
        ) : (
          <div className={viewMode === 'grid' ? "calendar-layout-grid" : ""}>

            {/* Monthly Grid Mode */}
            {viewMode === 'grid' && (
              <div className="calendar-grid-container">
                {/* Weekdays Label Header */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', fontWeight: 800, color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '0.9rem' }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} style={{ padding: '8px 0' }}>{day}</div>
                  ))}
                </div>

                {/* Days Grid */}
                <div className="calendar-month-grid">
                  {calendarDays.map((day, idx) => {
                    const dayEvents = getEventsForDay(day);
                    const isToday = day &&
                      new Date().getDate() === day &&
                      new Date().getMonth() === currentDate.getMonth() &&
                      new Date().getFullYear() === currentDate.getFullYear();

                    return (
                      <div
                        key={idx}
                        onClick={() => day && dayEvents.length > 0 && setSelectedDayEvents({ day, events: dayEvents })}
                        style={gridDayStyle(day, isToday, dayEvents.length > 0, dayEvents)}
                      >
                        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: isToday ? '#ffffff' : 'var(--text-primary)' }}>{day}</span>
                        {dayEvents.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%', marginTop: '6px' }}>
                            {dayEvents.slice(0, 2).map((ev, eIdx) => (
                              <div key={eIdx} style={ev.isHoliday ? holidayBadgeStyle : birthdayBadgeStyle} className="text-truncate">
                                {ev.summary}
                              </div>
                            ))}
                            {dayEvents.length > 2 && (
                              <span style={{ fontSize: '0.65rem', fontWeight: 750, color: 'var(--text-muted)', alignSelf: 'center' }}>
                                +{dayEvents.length - 2} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Event List Mode */}
            {viewMode === 'list' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                {events.filter(event => {
                  const eventStart = event.start.date || event.start.dateTime;
                  if (!eventStart) return false;
                  const evDate = new Date(eventStart);
                  return evDate.getMonth() === currentDate.getMonth() && evDate.getFullYear() === currentDate.getFullYear();
                }).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', backgroundColor: '#ffffff', borderRadius: '24px', border: '1px solid var(--border-color)' }}>
                    <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-secondary)' }}>No scheduled activities or holidays for this month.</p>
                  </div>
                ) : (
                  events
                    .filter(event => {
                      const eventStart = event.start.date || event.start.dateTime;
                      if (!eventStart) return false;
                      const evDate = new Date(eventStart);
                      return evDate.getMonth() === currentDate.getMonth() && evDate.getFullYear() === currentDate.getFullYear();
                    })
                    .sort((a, b) => new Date(a.start.date || a.start.dateTime) - new Date(b.start.date || b.start.dateTime))
                    .map(ev => (
                      <motion.div
                        key={ev.id}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        style={listCardStyle(ev.isHoliday)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <span style={ev.isHoliday ? listHolidayCategoryStyle : listBirthdayCategoryStyle}>
                              {ev.isHoliday ? 'Holiday' : 'Birthday'}
                            </span>
                            <h3 style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: '8px 0 6px', fontWeight: 800, fontFamily: 'Poppins' }}>{ev.summary}</h3>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{ev.description || 'No additional description provided.'}</p>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', minWidth: '120px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-secondary)' }}>
                              <Clock size={14} />
                              {new Date(ev.start.date || ev.start.dateTime).toLocaleDateString('default', { day: 'numeric', month: 'short' })}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                )}
              </div>
            )}

            {/* Selected Day Details (Grid Mode Details Panel) */}
            {viewMode === 'grid' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', minWidth: '280px' }}>
                <AnimatePresence mode="wait">
                  {selectedDayEvents ? (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '24px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)' }}
                    >
                      <h3 style={{ fontSize: '1.35rem', color: 'var(--text-primary)', marginBottom: '16px', fontFamily: 'Poppins', borderBottom: '2px solid var(--border-color)', paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Sparkles size={18} style={{ color: 'var(--primary-color)' }} /> Events for Day {selectedDayEvents.day}
                      </h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {selectedDayEvents.events.map(ev => (
                          <div key={ev.id} style={{ padding: '16px', borderRadius: '14px', backgroundColor: ev.isHoliday ? 'rgba(74,222,128,0.08)' : 'rgba(96,165,250,0.08)', borderLeft: `5px solid ${ev.isHoliday ? '#10B981' : '#1D4ED8'}` }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: ev.isHoliday ? '#15803D' : '#1D4ED8', textTransform: 'uppercase' }}>
                              {ev.isHoliday ? 'Indian Holiday' : 'Birthday Celebration'}
                            </span>
                            <h4 style={{ margin: '4px 0 6px', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'Poppins' }}>{ev.summary}</h4>

                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ backgroundColor: 'rgba(31, 41, 55, 0.02)', border: '2px dashed var(--border-color)', padding: '40px 24px', borderRadius: '24px', textAlign: 'center', color: 'var(--text-muted)' }}
                    >
                      <CalendarIcon size={32} style={{ margin: '0 auto 12px', display: 'block' }} />
                      <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700 }}>Click on any day in the grid with active markers to view detail descriptions.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
}

// Styling parameters
const navBtnStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  border: '1px solid var(--border-color)',
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s',
  color: 'var(--text-primary)'
};

const toggleBtnStyle = {
  padding: '8px 16px',
  fontSize: '0.85rem',
  fontWeight: 700,
  borderRadius: '9999px',
  border: '1px solid var(--border-color)',
  backgroundColor: '#ffffff',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'all 0.2s',
  fontFamily: 'Quicksand'
};

const activeToggleBtnStyle = {
  ...toggleBtnStyle,
  backgroundColor: 'var(--primary-color)',
  color: '#ffffff',
  borderColor: 'var(--primary-color)'
};

const gridDayStyle = (day, isToday, hasEvents, dayEvents) => {
  const containsHoliday = dayEvents.some(ev => ev.isHoliday);
  const containsBirthday = dayEvents.some(ev => ev.isBirthday);
  
  let borderTopColor = 'var(--border-color)';
  if (hasEvents && !isToday) {
    if (containsHoliday) borderTopColor = '#10B981'; // Green for holiday
    else if (containsBirthday) borderTopColor = '#1D4ED8'; // Blue for birthday
  }

  return {
    aspectRatio: '1/1',
    backgroundColor: isToday ? 'var(--primary-color)' : '#ffffff',
    borderRadius: '12px',
    border: isToday ? 'none' : '1px solid var(--border-color)',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    cursor: day && hasEvents ? 'pointer' : 'default',
    transition: 'all 0.2s',
    opacity: day ? 1 : 0,
    pointerEvents: day ? 'auto' : 'none',
    boxShadow: hasEvents && !isToday ? '0 4px 8px rgba(31,41,55,0.03)' : 'none',
    borderTop: hasEvents && !isToday ? `4px solid ${borderTopColor}` : isToday ? 'none' : '1px solid var(--border-color)'
  };
};

// Green for Holiday
const holidayBadgeStyle = {
  fontSize: '0.62rem',
  fontWeight: 800,
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: 'rgba(74,222,128,0.15)',
  color: '#15803D',
  maxWidth: '100%',
  textAlign: 'center'
};

// Blue for Birthday
const birthdayBadgeStyle = {
  fontSize: '0.62rem',
  fontWeight: 800,
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: 'rgba(96,165,250,0.15)',
  color: '#1D4ED8',
  maxWidth: '100%',
  textAlign: 'center'
};

const listCardStyle = (isHoliday) => ({
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: '16px',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-sm)',
  borderLeft: `6px solid ${isHoliday ? '#10B981' : '#1D4ED8'}`
});

const listHolidayCategoryStyle = {
  fontSize: '0.72rem',
  fontWeight: 800,
  color: '#15803D',
  backgroundColor: 'rgba(74,222,128,0.08)',
  padding: '4px 10px',
  borderRadius: '9999px',
  textTransform: 'uppercase',
  letterSpacing: '1px'
};

const listBirthdayCategoryStyle = {
  fontSize: '0.72rem',
  fontWeight: 800,
  color: '#1D4ED8',
  backgroundColor: 'rgba(96,165,250,0.08)',
  padding: '4px 10px',
  borderRadius: '9999px',
  textTransform: 'uppercase',
  letterSpacing: '1px'
};
